import React, { useId, useMemo } from 'react';
import { Tooltip } from 'antd';
import { Info } from 'lucide-react';
import PropTypes from 'prop-types';

import { useQuery } from '@tanstack/react-query';
import dayjs from '@tt/dates/dayjs';

import { Card } from '../../components/ui/card';
import PerformanceGapChart from '../../components/visualizations/performance-gap-chart';
import cnMerge from '../../lib/utils';
import { useManagementStore, useSettingStore, useUserState } from '../../store';
import {
    dashboard as dashboardDescriptions,
    statisticsPnL,
    statisticsSymbol,
    statisticsTrades
} from '../../utilities/descriptions';
import { DISCIPLINE, FORMAT, SUMMARY } from '../../utilities/functions';

const themeStyles = {
    light: {
        '--background': '0 0% 100%',
        '--foreground': '0 0% 4%',
        '--card': '0 0% 100%',
        '--card-foreground': '0 0% 4%',
        '--muted': '0 0% 96%',
        '--muted-foreground': '0 0% 45%',
        '--accent': '0 0% 96%',
        '--accent-foreground': '0 0% 9%',
        '--border': '0 0% 89%',
        '--input': '0 0% 89%',
        '--primary': '217 91% 60%',
        '--primary-foreground': '214 100% 97%',
        '--ring': '0 0% 64%'
    },
    dark: {
        '--background': '0 0% 3.9%',
        '--foreground': '0 0% 98%',
        '--card': '0 0% 8.5%',
        '--card-foreground': '0 0% 98%',
        '--muted': '0 0% 15%',
        '--muted-foreground': '0 0% 64%',
        '--accent': '0 0% 25%',
        '--accent-foreground': '0 0% 98%',
        '--border': '0 0% 100% / 0.1',
        '--input': '0 0% 100% / 0.15',
        '--primary': '217 91% 60%',
        '--primary-foreground': '226 72% 13%',
        '--ring': '0 0% 45%'
    }
};

const TONE_CONFIG = {
    success: {
        stroke: '#10b981',
        fill: '16, 185, 129',
        valueClassName: 'text-emerald-500',
        badgeIconClassName: 'text-emerald-500'
    },
    danger: {
        stroke: '#ef4444',
        fill: '239, 68, 68',
        valueClassName: 'text-red-500',
        badgeIconClassName: 'text-red-500'
    },
    neutral: {
        stroke: '#0f172a',
        fill: '15, 23, 42',
        valueClassName: 'text-foreground',
        badgeIconClassName: 'text-muted-foreground'
    }
};

const FIGMA_CARD_TINT =
    'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0) 62%, rgba(249,250,250,0.82) 84%, rgba(16,185,129,0.05) 100%)';
const FIGMA_CARD_TINT_DARK =
    'linear-gradient(180deg, rgba(24,24,27,0) 0%, rgba(24,24,27,0) 62%, rgba(52,56,60,0.22) 84%, rgba(16,185,129,0.08) 100%)';
const FIGMA_INFO_CARD_TINT =
    'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0) 68%, rgba(250,250,250,0.86) 88%, rgba(16,185,129,0.05) 100%)';
const FIGMA_INFO_CARD_TINT_DARK =
    'linear-gradient(180deg, rgba(24,24,27,0) 0%, rgba(24,24,27,0) 68%, rgba(52,56,60,0.18) 88%, rgba(16,185,129,0.08) 100%)';
const FIGMA_FONT_FAMILY = "'IBM Plex Sans', Inter, sans-serif";
const BAR_CHART_BAR_WIDTH = 50;
const BAR_CHART_GAP = 40;
const BAR_CHART_PLOT_TOP = 14;
const BAR_CHART_LABEL_GAP = 8;
const BAR_CHART_LABEL_HEIGHT = 16;
const FIGMA_TOOLTIP_OVERLAY_STYLE = {
    fontFamily: FIGMA_FONT_FAMILY,
    color: '#0a0a0a',
    fontSize: '12px',
    lineHeight: '18px',
    maxWidth: '240px',
    padding: '10px 12px'
};

const DASHBOARD_INFO = {
    disciplinePercent:
        'Percentage of evaluated trades that followed your trading rules (Cat 1 & Cat 2).',
    dailyWinRate: 'Winning days versus losing days across the current range.',
    performanceGap:
        'Compares cumulative P&L of disciplined trades (Cat 1 & 2) versus undisciplined trades (Cat 3 & 4).',
    totalLongTrades: 'Number of executed long trades within the current filter.',
    totalShortTrades: 'Number of executed short trades within the current filter.',
    totalClosedTrades: 'Number of trades that were fully closed within the current filter.',
    longestHoldTimeWinTrades:
        'The longest amount of time a winning trade was held within the current filter.',
    longestHoldTimeLossTrades:
        'The longest amount of time a losing trade was held within the current filter.',
    topGainerSymbol: 'The symbol that produced the highest net profit within the current filter.',
    topLoserSymbol: 'The symbol that produced the largest net loss within the current filter.',
    firstTradingDay: 'The first trading day represented in the current filter.',
    lastTradingDay: 'The most recent trading day represented in the current filter.'
};

const toNumber = (value) => {
    if (typeof value === 'number') return value;
    return FORMAT.CURRENCY_TO_NUMBER(value);
};

const formatPercent = (value, digits = 1) => {
    const parsed = Number(value || 0);

    if (!Number.isFinite(parsed)) return '--';

    const normalized = parsed.toFixed(digits).replace(/\.0$/, '');
    return `${normalized}%`;
};

const formatCurrency = (value) => FORMAT.NUMBER_TO_CURRENCY(Number(value || 0));

const formatAbsoluteCurrency = (value) => FORMAT.NUMBER_TO_CURRENCY(Math.abs(Number(value || 0)));

const labelFromDateRange = (range) => {
    if (!Array.isArray(range) || range.length !== 2) return 'All Time';
    return `${dayjs(range[0]).format('MMM DD, YYYY')} - ${dayjs(range[1]).format('MMM DD, YYYY')}`;
};

const normalizePointSeries = (series, maxPoints = 14) => {
    if (!Array.isArray(series)) return [];

    return series
        .map((point, index) => {
            const raw =
                typeof point === 'number'
                    ? point
                    : (point?.y ??
                      point?.value ??
                      point?.totalNetPnLPerDay ??
                      point?.totalAccumulatedPnLPerDay ??
                      point?.cumulativePnl);
            const parsed = Number(raw);

            if (!Number.isFinite(parsed)) return null;

            return {
                x: index,
                y: parsed,
                label: point?.x || point?.date || String(index)
            };
        })
        .filter(Boolean)
        .slice(-maxPoints);
};

const normalizeDailyBars = (series, maxPoints = 7) => {
    if (!Array.isArray(series)) return [];

    return series
        .map((point, index) => ({
            label: point?.date || point?.x || String(index),
            positive: Math.max(Number(point?.totalNetPnLPerDay || 0), 0),
            negative: Math.abs(Math.min(Number(point?.totalNetPnLPerDay || 0), 0)),
            wins: Math.max(Number(point?.totalWinsPerDay || 0), 0),
            losses: Math.max(Number(point?.totalLossesPerDay || 0), 0)
        }))
        .slice(-maxPoints);
};

const buildWaveSeries = (series, maxPoints, mapper) => {
    if (!Array.isArray(series)) return [];

    return series
        .slice(-maxPoints)
        .map((point, index) => ({
            x: index,
            y: Number(mapper(point, index) || 0),
            label: point?.date || point?.x || String(index)
        }))
        .filter((point) => Number.isFinite(point.y));
};

const getCardSurfaceStyle = (variant, darkMode) => {
    let backgroundImage = 'none';

    if (variant === 'info') {
        backgroundImage = darkMode ? FIGMA_INFO_CARD_TINT_DARK : FIGMA_INFO_CARD_TINT;
    } else if (variant !== 'plain') {
        backgroundImage = darkMode ? FIGMA_CARD_TINT_DARK : FIGMA_CARD_TINT;
    }

    return {
        backgroundColor: darkMode ? '#171717' : '#ffffff',
        backgroundImage
    };
};

const getBarChartWidth = (pointCount) => {
    if (pointCount <= 0) return BAR_CHART_BAR_WIDTH;
    return pointCount * BAR_CHART_BAR_WIDTH + Math.max(pointCount - 1, 0) * BAR_CHART_GAP;
};

const getChartCoordinates = (points, width, height, padding) => {
    if (!points.length) return [];

    const values = points.map((point) => point.y);
    const minY = Math.min(...values);
    const maxY = Math.max(...values);
    const rangeY = maxY - minY || 1;
    const innerWidth = width - padding.left - padding.right;
    const innerHeight = height - padding.top - padding.bottom;

    return points.map((point, index) => ({
        x: padding.left + (index / Math.max(points.length - 1, 1)) * innerWidth,
        y: padding.top + ((maxY - point.y) / rangeY) * innerHeight,
        label: point.label,
        value: point.y
    }));
};

const getControlPoint = (current, previous, next, reverse = false) => {
    const p = previous || current;
    const n = next || current;
    const smoothing = 0.18;
    const angle = Math.atan2(n.y - p.y, n.x - p.x) + (reverse ? Math.PI : 0);
    const length = Math.sqrt((n.x - p.x) ** 2 + (n.y - p.y) ** 2) * smoothing;

    return {
        x: current.x + Math.cos(angle) * length,
        y: current.y + Math.sin(angle) * length
    };
};

const buildSmoothPath = (coordinates) => {
    if (!coordinates.length) return '';
    if (coordinates.length === 1) return `M ${coordinates[0].x} ${coordinates[0].y}`;

    return coordinates.reduce((path, point, index, points) => {
        if (index === 0) return `M ${point.x} ${point.y}`;

        const prev = points[index - 1];
        const startControl = getControlPoint(prev, points[index - 2], point);
        const endControl = getControlPoint(point, prev, points[index + 1], true);

        return `${path} C ${startControl.x} ${startControl.y}, ${endControl.x} ${endControl.y}, ${point.x} ${point.y}`;
    }, '');
};

const buildAreaPath = (coordinates, baselineY) => {
    if (!coordinates.length) return '';

    const linePath = buildSmoothPath(coordinates);
    const last = coordinates[coordinates.length - 1];
    const first = coordinates[0];

    return `${linePath} L ${last.x} ${baselineY} L ${first.x} ${baselineY} Z`;
};

const getAxisPoints = (points, maxLabels = 6) => {
    if (points.length <= maxLabels) return points;

    const lastIndex = points.length - 1;
    const indexes = new Set();

    for (let index = 0; index < maxLabels; index += 1) {
        indexes.add(Math.round((index / (maxLabels - 1)) * lastIndex));
    }

    return Array.from(indexes)
        .sort((left, right) => left - right)
        .map((index) => points[index]);
};

function InfoGlyph({ className, description }) {
    const icon = (
        <Info className={cnMerge('h-4 w-4 text-[#a3a3a3]', className)} strokeWidth={1.8} />
    );

    if (!description) return icon;

    return (
        <Tooltip
            color="white"
            overlayInnerStyle={FIGMA_TOOLTIP_OVERLAY_STYLE}
            title={description}
            trigger={['hover', 'click']}
        >
            <button
                type="button"
                className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-transparent p-0 align-middle"
                aria-label={description}
            >
                {icon}
            </button>
        </Tooltip>
    );
}

InfoGlyph.propTypes = {
    className: PropTypes.string,
    description: PropTypes.string
};

InfoGlyph.defaultProps = {
    className: undefined,
    description: undefined
};

function SoftAreaChart({ points, tone, height, showAxis, labelCount, gridLines, className }) {
    const gradientId = useId().replace(/:/g, '-');
    const chartHeight = height;
    const axisHeight = showAxis ? 28 : 0;
    const plotHeight = Math.max(chartHeight - axisHeight, 1);
    const width = 640;
    const padding = showAxis
        ? { top: 12, right: 0, bottom: 0, left: 0 }
        : { top: 10, right: 0, bottom: 2, left: 0 };
    const coordinates = getChartCoordinates(points, width, plotHeight, padding);
    const baselineY = plotHeight - padding.bottom;
    const axisPoints = getAxisPoints(coordinates, labelCount);
    const grid = Array.from({ length: gridLines }, (_, index) => {
        const progress = gridLines === 1 ? 1 : index / (gridLines - 1);
        return padding.top + progress * (baselineY - padding.top);
    });
    const palette = TONE_CONFIG[tone] || TONE_CONFIG.success;

    if (!coordinates.length) {
        return (
            <div
                className={cnMerge(
                    'flex h-full items-center justify-center text-xs text-muted-foreground',
                    className
                )}
            >
                No chart data
            </div>
        );
    }

    return (
        <div className={cnMerge('flex h-full flex-col', className)}>
            <svg
                viewBox={`0 0 ${width} ${plotHeight}`}
                className="w-full flex-1"
                preserveAspectRatio="none"
            >
                <defs>
                    <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={`rgba(${palette.fill}, 0.14)`} />
                        <stop offset="100%" stopColor={`rgba(${palette.fill}, 0)`} />
                    </linearGradient>
                </defs>
                {grid.map((line) => (
                    <line
                        key={`${gradientId}-${line}`}
                        x1={padding.left}
                        x2={width - padding.right}
                        y1={line}
                        y2={line}
                        stroke="rgba(212, 212, 212, 0.8)"
                        strokeDasharray="4 4"
                        strokeWidth="1"
                    />
                ))}
                <path d={buildAreaPath(coordinates, baselineY)} fill={`url(#${gradientId})`} />
                <path
                    d={buildSmoothPath(coordinates)}
                    fill="none"
                    stroke={palette.stroke}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                />
            </svg>
            {showAxis && (
                <div className="-mx-3 flex h-7 items-end justify-between px-5 text-[12px] font-normal leading-none text-[#0a0a0a]">
                    {axisPoints.map((point) => (
                        <span key={`${gradientId}-${point.x}-${point.label}`}>
                            {dayjs(point.label).isValid()
                                ? dayjs(point.label).format('MM/DD')
                                : point.label}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}

function StatCard({ title, value, helper, infoDescription, tone, darkMode }) {
    return (
        <Card
            className="min-h-[124px] rounded-[14px] border border-[#e5e5e5] bg-card shadow-none dark:border-white/10"
            style={getCardSurfaceStyle('plain', darkMode)}
        >
            <div className="flex h-full flex-col gap-2 p-6">
                <div className="flex items-center justify-between text-[14px] font-medium leading-5 text-[#0a0a0a]">
                    <span>{title}</span>
                    <InfoGlyph description={infoDescription} />
                </div>
                <div
                    className={cnMerge(
                        'w-full text-left text-[24px] font-bold leading-8 tracking-normal',
                        TONE_CONFIG[tone]?.valueClassName || TONE_CONFIG.neutral.valueClassName
                    )}
                >
                    {value}
                </div>
                <div className="w-full text-left text-[12px] font-normal leading-4 text-[#737373]">
                    {helper}
                </div>
            </div>
        </Card>
    );
}

function SparkCard({
    title,
    value,
    tone,
    valueTone,
    infoDescription,
    points,
    showChart,
    darkMode
}) {
    if (!showChart) {
        return (
            <Card
                className="min-h-[124px] overflow-hidden rounded-[14px] border border-[#e5e5e5] bg-card shadow-none dark:border-white/10"
                style={getCardSurfaceStyle('tinted', darkMode)}
            >
                <div className="flex h-full flex-col px-6 py-4">
                    <div className="flex items-center justify-between text-[14px] font-medium leading-5 text-[#0a0a0a]">
                        <span>{title}</span>
                        <InfoGlyph description={infoDescription} />
                    </div>
                    <div className="flex flex-1 items-center">
                        <div
                            className={cnMerge(
                                'w-full text-left text-[30px] font-semibold leading-9 tracking-normal',
                                TONE_CONFIG[valueTone]?.valueClassName || 'text-card-foreground'
                            )}
                        >
                            {value}
                        </div>
                    </div>
                </div>
            </Card>
        );
    }

    return (
        <Card
            className="min-h-[210px] overflow-hidden rounded-[14px] border border-[#e5e5e5] bg-card shadow-none dark:border-white/10"
            style={getCardSurfaceStyle('tinted', darkMode)}
        >
            <div
                className={cnMerge(
                    'flex h-full flex-col',
                    showChart ? 'justify-between' : 'justify-start'
                )}
            >
                <div className="flex flex-col gap-1.5 p-6">
                    <div className="flex items-center justify-between text-[14px] font-medium leading-5 text-[#0a0a0a]">
                        <span>{title}</span>
                        <InfoGlyph description={infoDescription} />
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5">
                        <div
                            className={cnMerge(
                                'text-[30px] font-semibold leading-9 tracking-normal',
                                TONE_CONFIG[valueTone]?.valueClassName || 'text-card-foreground'
                            )}
                        >
                            {value}
                        </div>
                    </div>
                </div>
                <div className="h-[100px] w-full">
                    <SoftAreaChart
                        points={points}
                        tone={tone}
                        height={100}
                        showAxis={false}
                        labelCount={0}
                        gridLines={0}
                        className="h-full w-full"
                    />
                </div>
            </div>
        </Card>
    );
}

function RatioCard({ value, infoDescription, winnerValue, loserValue, darkMode }) {
    const total = Math.max(Number(winnerValue || 0) + Number(loserValue || 0), 1);
    const winnerWidth = (Number(winnerValue || 0) / total) * 100;

    return (
        <Card
            className="min-h-[124px] overflow-hidden rounded-[14px] border border-[#e5e5e5] bg-card shadow-none dark:border-white/10"
            style={getCardSurfaceStyle('tinted', darkMode)}
        >
            <div className="flex h-full flex-col px-6 py-4">
                <div className="flex items-center justify-between text-[14px] font-medium leading-5 text-[#0a0a0a]">
                    <span>Average Winner/Loser</span>
                    <InfoGlyph description={infoDescription} />
                </div>
                <div className="flex flex-1 items-center">
                    <div className="w-full text-left text-[30px] font-semibold leading-9 tracking-normal text-card-foreground">
                        {value}
                    </div>
                </div>
                <div className="max-w-full pt-2">
                    <div className="h-[22px] w-[175.25px] max-w-full overflow-hidden rounded-[6px] bg-background">
                        <div className="flex h-full w-full">
                            <Tooltip
                                color="white"
                                mouseEnterDelay={0.05}
                                overlayInnerStyle={FIGMA_TOOLTIP_OVERLAY_STYLE}
                                placement="top"
                                title={
                                    <div className="flex flex-col gap-1">
                                        <div className="text-[12px] leading-4 text-[#525252]">
                                            Average Winner
                                        </div>
                                        <div className="text-[12px] font-semibold leading-4 text-[#0a0a0a]">
                                            {formatCurrency(winnerValue)}
                                        </div>
                                    </div>
                                }
                                trigger={['hover']}
                            >
                                <div
                                    className="h-full cursor-default bg-emerald-500"
                                    style={{ width: `${winnerWidth}%` }}
                                    aria-label={`Average Winner ${formatCurrency(winnerValue)}`}
                                />
                            </Tooltip>
                            <Tooltip
                                color="white"
                                mouseEnterDelay={0.05}
                                overlayInnerStyle={FIGMA_TOOLTIP_OVERLAY_STYLE}
                                placement="top"
                                title={
                                    <div className="flex flex-col gap-1">
                                        <div className="text-[12px] leading-4 text-[#525252]">
                                            Average Loser
                                        </div>
                                        <div className="text-[12px] font-semibold leading-4 text-[#0a0a0a]">
                                            {formatAbsoluteCurrency(loserValue)}
                                        </div>
                                    </div>
                                }
                                trigger={['hover']}
                            >
                                <div
                                    className="h-full cursor-default bg-red-500"
                                    style={{ width: `${100 - winnerWidth}%` }}
                                    aria-label={`Average Loser ${formatAbsoluteCurrency(
                                        loserValue
                                    )}`}
                                />
                            </Tooltip>
                        </div>
                    </div>
                </div>
            </div>
        </Card>
    );
}

function AreaChartCard({ title, points, tone, infoDescription, darkMode }) {
    return (
        <Card
            className="min-h-[286px] overflow-hidden rounded-[10px] border border-[#e5e5e5] bg-card shadow-none dark:border-white/10"
            style={getCardSurfaceStyle('tinted', darkMode)}
        >
            <div className="flex h-full flex-col">
                <div className="flex items-center justify-between p-6 pb-0 text-[14px] font-medium leading-5 text-[#0a0a0a]">
                    <span>{title}</span>
                    <InfoGlyph description={infoDescription} />
                </div>
                <div className="h-[218px] w-full px-[7px] pb-5 pt-[17px]">
                    <SoftAreaChart
                        points={points}
                        tone={tone}
                        height={182}
                        showAxis
                        labelCount={6}
                        gridLines={5}
                        className="h-full w-full"
                    />
                </div>
            </div>
        </Card>
    );
}

function PerformanceGapChartCard({ title, data, infoDescription, darkMode }) {
    return (
        <Card
            className="min-h-[286px] overflow-hidden rounded-[10px] border border-[#e5e5e5] bg-card shadow-none dark:border-white/10"
            style={getCardSurfaceStyle('tinted', darkMode)}
        >
            <div className="flex h-full flex-col">
                <div className="flex items-center justify-between p-6 pb-0 text-[14px] font-medium leading-5 text-[#0a0a0a]">
                    <span>{title}</span>
                    <InfoGlyph description={infoDescription} />
                </div>
                <div className="h-[236px] w-full px-2 pb-2 pt-2">
                    <PerformanceGapChart data={data} />
                </div>
            </div>
        </Card>
    );
}

function PositiveNegativeBarCard({
    title,
    points,
    positiveKey,
    negativeKey,
    infoDescription,
    darkMode
}) {
    const positiveMax = Math.max(...points.map((point) => Number(point[positiveKey] || 0)), 1);
    const negativeMax = Math.max(...points.map((point) => Number(point[negativeKey] || 0)), 1);
    const isPnlChart = positiveKey === 'positive' && negativeKey === 'negative';
    const positiveLabel = isPnlChart ? 'Profit' : 'Wins';
    const negativeLabel = isPnlChart ? 'Loss' : 'Losses';
    const summaryLabel = isPnlChart ? 'Net' : 'Win %';
    const sharedCountMax = Math.max(
        ...points.map((point) =>
            Math.max(Number(point[positiveKey] || 0), Number(point[negativeKey] || 0))
        ),
        1
    );
    const positiveScaleMax = isPnlChart ? positiveMax : sharedCountMax;
    const negativeScaleMax = isPnlChart ? negativeMax : sharedCountMax;
    const positiveRegionHeight = isPnlChart ? 110 : 87;
    const negativeRegionHeight = isPnlChart ? 64 : 87;
    const minimumBarHeight = isPnlChart ? 24 : 0;
    const plotHeight = positiveRegionHeight + negativeRegionHeight;
    const contentHeight =
        BAR_CHART_PLOT_TOP + plotHeight + BAR_CHART_LABEL_GAP + BAR_CHART_LABEL_HEIGHT;
    const zeroLineOffset = BAR_CHART_PLOT_TOP + positiveRegionHeight;
    const chartWidth = getBarChartWidth(points.length);
    return (
        <Card
            className="min-h-[286px] overflow-hidden rounded-[10px] border border-[#e5e5e5] bg-card shadow-none dark:border-white/10"
            style={getCardSurfaceStyle('tinted', darkMode)}
        >
            <div className="flex h-full flex-col">
                <div className="flex items-center justify-between p-6 pb-0 text-[14px] font-medium leading-5 text-[#0a0a0a]">
                    <span>{title}</span>
                    <InfoGlyph description={infoDescription} />
                </div>
                <div className="relative h-[218px] pt-0">
                    <div
                        className="pointer-events-none absolute inset-x-0 border-t border-dashed border-[#a3a3a3]/70"
                        style={{ top: `${zeroLineOffset}px` }}
                    />
                    <div className="px-[28px]">
                        <div className="overflow-x-auto overflow-y-hidden">
                            <div
                                className="mx-auto min-w-full"
                                style={{ width: `${chartWidth}px` }}
                            >
                                <div
                                    className="flex justify-center gap-[40px]"
                                    style={{
                                        minHeight: `${contentHeight}px`,
                                        paddingTop: `${BAR_CHART_PLOT_TOP}px`
                                    }}
                                >
                                    {points.map((point) => {
                                        const positiveValue = Number(point[positiveKey] || 0);
                                        const negativeValue = Number(point[negativeKey] || 0);
                                        const dateLabel = dayjs(point.label).isValid()
                                            ? dayjs(point.label).format('MM/DD')
                                            : point.label;
                                        const totalValue = positiveValue + negativeValue;
                                        const positiveDisplayValue = isPnlChart
                                            ? formatCurrency(positiveValue)
                                            : positiveValue;
                                        const negativeDisplayValue = isPnlChart
                                            ? formatAbsoluteCurrency(negativeValue)
                                            : negativeValue;
                                        const positiveHeight =
                                            positiveValue > 0
                                                ? Math.max(
                                                      (positiveValue / positiveScaleMax) *
                                                          positiveRegionHeight,
                                                      minimumBarHeight
                                                  )
                                                : 0;
                                        const negativeHeight =
                                            negativeValue > 0
                                                ? Math.max(
                                                      (negativeValue / negativeScaleMax) *
                                                          negativeRegionHeight,
                                                      minimumBarHeight
                                                  )
                                                : 0;
                                        let summaryValue = '--';

                                        if (isPnlChart) {
                                            summaryValue = formatCurrency(
                                                positiveValue - negativeValue
                                            );
                                        } else if (totalValue > 0) {
                                            summaryValue = formatPercent(
                                                (positiveValue / totalValue) * 100,
                                                0
                                            );
                                        }

                                        return (
                                            <Tooltip
                                                key={`${title}-${point.label}`}
                                                color="white"
                                                mouseEnterDelay={0.05}
                                                overlayInnerStyle={FIGMA_TOOLTIP_OVERLAY_STYLE}
                                                placement="top"
                                                title={
                                                    <div className="flex min-w-[132px] flex-col gap-2">
                                                        <div className="text-[12px] font-semibold leading-4 text-[#0a0a0a]">
                                                            {dateLabel}
                                                        </div>
                                                        {(!isPnlChart || positiveValue > 0) && (
                                                            <div className="flex items-center justify-between gap-4 text-[12px] leading-4 text-[#525252]">
                                                                <span className="inline-flex items-center gap-1.5">
                                                                    <span className="h-2 w-2 rounded-full bg-emerald-500" />
                                                                    {positiveLabel}
                                                                </span>
                                                                <span className="font-medium text-[#0a0a0a]">
                                                                    {positiveDisplayValue}
                                                                </span>
                                                            </div>
                                                        )}
                                                        {(!isPnlChart || negativeValue > 0) && (
                                                            <div className="flex items-center justify-between gap-4 text-[12px] leading-4 text-[#525252]">
                                                                <span className="inline-flex items-center gap-1.5">
                                                                    <span className="h-2 w-2 rounded-full bg-red-500" />
                                                                    {negativeLabel}
                                                                </span>
                                                                <span className="font-medium text-[#0a0a0a]">
                                                                    {negativeDisplayValue}
                                                                </span>
                                                            </div>
                                                        )}
                                                        {!isPnlChart && (
                                                            <div className="flex items-center justify-between gap-4 border-t border-[#e5e5e5] pt-2 text-[12px] leading-4 text-[#525252]">
                                                                <span>{summaryLabel}</span>
                                                                <span className="font-semibold text-[#0a0a0a]">
                                                                    {summaryValue}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                }
                                                trigger={['hover']}
                                            >
                                                <div className="flex w-[50px] cursor-default flex-col items-center">
                                                    <div
                                                        className="relative w-[50px]"
                                                        style={{ height: `${plotHeight}px` }}
                                                    >
                                                        {positiveValue > 0 && (
                                                            <div
                                                                className="absolute left-0 right-0 rounded-t-[8px] bg-emerald-500"
                                                                style={{
                                                                    bottom: `${negativeRegionHeight}px`,
                                                                    height: `${positiveHeight}px`
                                                                }}
                                                            />
                                                        )}
                                                        {negativeValue > 0 && (
                                                            <div
                                                                className="absolute left-0 right-0 rounded-b-[8px] bg-red-500"
                                                                style={{
                                                                    top: `${positiveRegionHeight}px`,
                                                                    height: `${negativeHeight}px`
                                                                }}
                                                            />
                                                        )}
                                                    </div>
                                                    <span
                                                        className="flex items-end text-[12px] font-normal leading-none text-[#0a0a0a]"
                                                        style={{
                                                            height: `${BAR_CHART_LABEL_HEIGHT}px`,
                                                            marginTop: `${BAR_CHART_LABEL_GAP}px`
                                                        }}
                                                    >
                                                        {dateLabel}
                                                    </span>
                                                </div>
                                            </Tooltip>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Card>
    );
}

function InfoTableCard({ title, description, rows, darkMode, className }) {
    return (
        <Card
            className={cnMerge(
                'min-h-[426px] rounded-[14px] border border-[#e5e5e5] bg-card shadow-none dark:border-white/10',
                className
            )}
            style={getCardSurfaceStyle('info', darkMode)}
        >
            <div className="flex h-full flex-col gap-6 p-6">
                <div className="flex w-full flex-col items-start gap-1.5 text-left">
                    <div className="w-full text-left text-[16px] font-semibold leading-6 tracking-normal text-[#0a0a0a]">
                        {title}
                    </div>
                    <div className="w-full text-left text-[14px] font-normal leading-5 text-[#737373]">
                        {description}
                    </div>
                </div>
                <div className="flex flex-1 flex-col text-left">
                    {rows.map((row, index) => (
                        <React.Fragment key={`${title}-${row.label}`}>
                            <div className="flex min-h-6 items-center justify-between gap-4">
                                <span className="inline-flex min-w-0 flex-1 items-center gap-1 text-[14px] font-medium leading-5 text-[#0a0a0a]">
                                    <span className="min-w-0 text-left">{row.label}</span>
                                    <InfoGlyph
                                        className="h-4 w-4 shrink-0"
                                        description={row.description}
                                    />
                                </span>
                                <span className="shrink-0 pl-4 text-right text-[16px] font-medium leading-6 text-[#0a0a0a]">
                                    {row.value}
                                </span>
                            </div>
                            {index < rows.length - 1 && (
                                <div className="my-2 h-px w-full bg-[#e5e5e5]" />
                            )}
                        </React.Fragment>
                    ))}
                </div>
            </div>
        </Card>
    );
}

SoftAreaChart.propTypes = {
    points: PropTypes.arrayOf(
        PropTypes.shape({
            x: PropTypes.number,
            y: PropTypes.number,
            label: PropTypes.string
        })
    ),
    tone: PropTypes.oneOf(['success', 'danger', 'neutral']),
    height: PropTypes.number,
    showAxis: PropTypes.bool,
    labelCount: PropTypes.number,
    gridLines: PropTypes.number,
    className: PropTypes.string
};

SoftAreaChart.defaultProps = {
    points: [],
    tone: 'success',
    height: 180,
    showAxis: false,
    labelCount: 0,
    gridLines: 0,
    className: undefined
};

StatCard.propTypes = {
    title: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    helper: PropTypes.string.isRequired,
    infoDescription: PropTypes.string,
    tone: PropTypes.oneOf(['success', 'danger', 'neutral']),
    darkMode: PropTypes.bool
};

StatCard.defaultProps = {
    infoDescription: undefined,
    tone: 'neutral',
    darkMode: false
};

SparkCard.propTypes = {
    title: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    tone: PropTypes.oneOf(['success', 'danger', 'neutral']),
    valueTone: PropTypes.oneOf(['success', 'danger', 'neutral']),
    infoDescription: PropTypes.string,
    points: PropTypes.array,
    showChart: PropTypes.bool,
    darkMode: PropTypes.bool
};

SparkCard.defaultProps = {
    tone: 'success',
    valueTone: 'neutral',
    infoDescription: undefined,
    points: [],
    showChart: true,
    darkMode: false
};

RatioCard.propTypes = {
    value: PropTypes.string.isRequired,
    infoDescription: PropTypes.string,
    winnerValue: PropTypes.number,
    loserValue: PropTypes.number,
    darkMode: PropTypes.bool
};

RatioCard.defaultProps = {
    infoDescription: undefined,
    winnerValue: 0,
    loserValue: 0,
    darkMode: false
};

AreaChartCard.propTypes = {
    title: PropTypes.string.isRequired,
    points: PropTypes.array,
    tone: PropTypes.oneOf(['success', 'danger', 'neutral']),
    infoDescription: PropTypes.string,
    darkMode: PropTypes.bool
};

AreaChartCard.defaultProps = {
    points: [],
    tone: 'success',
    infoDescription: undefined,
    darkMode: false
};

PerformanceGapChartCard.propTypes = {
    title: PropTypes.string.isRequired,
    data: PropTypes.shape({
        disciplinedCurve: PropTypes.array,
        undisciplinedCurve: PropTypes.array,
        summary: PropTypes.object
    }),
    infoDescription: PropTypes.string,
    darkMode: PropTypes.bool
};

PerformanceGapChartCard.defaultProps = {
    data: null,
    infoDescription: undefined,
    darkMode: false
};

PositiveNegativeBarCard.propTypes = {
    title: PropTypes.string.isRequired,
    points: PropTypes.array,
    positiveKey: PropTypes.oneOf(['positive', 'wins']).isRequired,
    negativeKey: PropTypes.oneOf(['negative', 'losses']).isRequired,
    infoDescription: PropTypes.string,
    darkMode: PropTypes.bool
};

PositiveNegativeBarCard.defaultProps = {
    points: [],
    infoDescription: undefined,
    darkMode: false
};

InfoTableCard.propTypes = {
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    rows: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            description: PropTypes.string
        })
    ).isRequired,
    darkMode: PropTypes.bool,
    className: PropTypes.string
};

InfoTableCard.defaultProps = {
    darkMode: false,
    className: undefined
};

export default function Dashboard() {
    const { filter } = useSettingStore((state) => state);
    const { publicView, company } = useManagementStore((state) => state);
    const { getUser } = useUserState();
    const { darkMode } = getUser();

    const modifiedFilter = { ...filter, dateRange: null, symbols: [], getAllofTickers: true };
    const queryFilter = publicView.isActive ? modifiedFilter : filter;
    const canFetchSummary = Array.isArray(filter.accounts) && filter.accounts.length > 0;

    const { isLoading, data: summary } = useQuery(
        ['summary-account', queryFilter],
        () => SUMMARY.fetchSummary(queryFilter),
        { enabled: canFetchSummary }
    );

    const { data: disciplineData } = useQuery(
        ['discipline-stats', queryFilter],
        () => DISCIPLINE.fetchDisciplineStats(queryFilter),
        { enabled: canFetchSummary && !publicView.isActive }
    );

    const { data: performanceGapData } = useQuery(
        ['performance-gap', queryFilter],
        () => DISCIPLINE.fetchPerformanceGap(queryFilter),
        { enabled: canFetchSummary && !publicView.isActive }
    );

    const dailySeries = useMemo(
        () => normalizePointSeries(summary?.dailyData || [], 30),
        [summary]
    );

    const runningPnlSeries = useMemo(
        () => normalizePointSeries(summary?.PnLPerDayAccumulatedFormatted || [], 30),
        [summary]
    );

    const dailyBarPoints = useMemo(
        () => normalizeDailyBars(summary?.dailyData || [], 7),
        [summary]
    );

    if (isLoading) return null;

    if (!summary) {
        return (
            <div className="no-data flex flex-col gap-2">
                <div className="text-2xl font-semibold">No data available.</div>
                <div className="text-sm text-muted-foreground">
                    Select different filters or upload trade logs by using the{' '}
                    <strong>Import Trades</strong> button.
                </div>
            </div>
        );
    }

    const winRate = summary.totalTrades > 0 ? (summary.totalWins / summary.totalTrades) * 100 : 0;
    const activeDays = summary.dailyData?.length || 0;
    const dayWinRate = activeDays > 0 ? (summary.totalWinsPerDay / activeDays) * 100 : 0;
    const averageWinner = Number(summary.averageNetPnLWin || 0);
    const averageLoser = Math.abs(Number(summary.averageNetPnLLoss || 0));
    const averageTradesPerDay =
        activeDays > 0 ? summary.totalTrades / activeDays : summary.totalTrades;
    const averageWinnerLoserRatio =
        averageLoser === 0 ? averageWinner : averageWinner / averageLoser;
    const largestLoss = Math.abs(toNumber(summary.minPnL));
    const rangeLabel = labelFromDateRange(filter.dateRange);
    const disciplinePercent = Number(disciplineData?.disciplinePercentage || 0);
    const disciplineEvaluatedTrades = Number.isFinite(Number(disciplineData?.evaluatedTrades))
        ? Number(disciplineData.evaluatedTrades)
        : 0;
    const performanceGapChartData = performanceGapData?.data || performanceGapData || null;

    const averageWinnerSeries = buildWaveSeries(
        summary.dailyData || [],
        12,
        (point, index) =>
            Math.max(
                Number(point?.totalWinsPerDay || 0) * Math.max(averageWinner, 1),
                averageWinner * 0.45
            ) + (index % 3 === 0 ? averageWinner * 0.05 : 0)
    );

    const averageLoserSeries = buildWaveSeries(
        summary.dailyData || [],
        12,
        (point, index) =>
            Math.max(
                Number(point?.totalLossesPerDay || 0) * Math.max(averageLoser, 1),
                averageLoser * 0.42
            ) + (index % 4 === 0 ? averageLoser * 0.04 : 0)
    );

    const largestLossSeries = buildWaveSeries(summary.dailyData || [], 12, (point, index) => {
        const base = Math.abs(Math.min(Number(point?.totalNetPnLPerDay || 0), 0));
        return Math.max(base, largestLoss * 0.52) + (index % 5 === 0 ? largestLoss * 0.02 : 0);
    });

    const tradesRows = [
        {
            label: 'Total gain/loss',
            value: summary.netPnL,
            description: dashboardDescriptions.totalGainLoss
        },
        {
            label: 'Average gain/loss per trade',
            value: summary.avgPnL,
            description: dashboardDescriptions.averageGainLossPerTrade
        },
        {
            label: 'Average daily gain/loss',
            value: summary.avgPnLPerDay,
            description: statisticsPnL.averagePnLPerDay
        },
        {
            label: 'Total number of trades',
            value: summary.totalTrades,
            description: dashboardDescriptions.totalNumberOfTrades
        },
        {
            label: 'Number of long trades',
            value: summary.totalLong,
            description: DASHBOARD_INFO.totalLongTrades
        },
        {
            label: 'Number of short trades',
            value: summary.totalShort,
            description: DASHBOARD_INFO.totalShortTrades
        },
        {
            label: 'Number of open trades',
            value: summary.totalOpenTrades,
            description: statisticsTrades.totalOpenTrades
        },
        {
            label: 'Number of closed trades',
            value: summary.totalClosedTrades,
            description: DASHBOARD_INFO.totalClosedTrades
        }
    ];

    const performanceRows = [
        {
            label: 'Number of winning trades',
            value: summary.totalWins,
            description: dashboardDescriptions.numberOfWinningTrades
        },
        {
            label: 'Number of losing trades',
            value: summary.totalLosses,
            description: dashboardDescriptions.numberOfLosingTrades
        },
        {
            label: 'Max consecutive wins',
            value: summary.maxConsecutiveWins,
            description: dashboardDescriptions.maxConsecutiveWins
        },
        {
            label: 'Max consecutive losses',
            value: summary.maxConsecutiveLosses,
            description: dashboardDescriptions.maxConsecutiveLosses
        },
        {
            label: 'Average hold time (winning trades)',
            value: summary.avgTradeDurationPerWin,
            description: dashboardDescriptions.averageHoldTimeWinTrades
        },
        {
            label: 'Average hold time (losing trades)',
            value: summary.avgTradeDurationPerLoss,
            description: dashboardDescriptions.averageHoldTimeLossTrades
        },
        {
            label: 'Longest hold time (winning trades)',
            value: summary.longestTradeDurationPerWin,
            description: DASHBOARD_INFO.longestHoldTimeWinTrades
        },
        {
            label: 'Longest hold time (losing trades)',
            value: summary.longestTradeDurationPerLoss,
            description: DASHBOARD_INFO.longestHoldTimeLossTrades
        }
    ];

    const symbolsRows = [
        {
            label: 'Most traded symbol',
            value: summary.maxTradesPerSymbol.symbol || '-',
            description: statisticsSymbol.mostTraded
        },
        {
            label: 'Top gainer symbol',
            value: `${summary.mostProfitableByPnL.symbol ?? '-'} ${
                summary.mostProfitableByPnL.PnL !== '-' ? summary.mostProfitableByPnL.PnL : ''
            }`.trim(),
            description: DASHBOARD_INFO.topGainerSymbol
        },
        {
            label: 'Top loser symbol',
            value: `${summary.leastProfitableByPnL.symbol ?? '-'} ${
                summary.leastProfitableByPnL.PnL !== '-' ? summary.leastProfitableByPnL.PnL : ''
            }`.trim(),
            description: DASHBOARD_INFO.topLoserSymbol
        },
        {
            label: 'Win/Loss Ratio',
            value: summary.winLossRatio,
            description: statisticsTrades.winLossRatio
        },
        {
            label: 'First trading day',
            value: summary.firstTradeDay,
            description: DASHBOARD_INFO.firstTradingDay
        },
        {
            label: 'Last trading day',
            value: summary.lastTradeDay,
            description: DASHBOARD_INFO.lastTradingDay
        },
        {
            label: 'Total commissions & fees',
            value: summary.totalCommissions,
            description: dashboardDescriptions.totalCommissionsFees
        }
    ];

    return (
        <div
            className={cnMerge(
                'w-full self-stretch bg-background px-6 py-4 text-foreground sm:py-5 xl:px-8',
                darkMode && 'dark'
            )}
            style={{
                ...(darkMode ? themeStyles.dark : themeStyles.light),
                fontFamily: "'IBM Plex Sans', Inter, sans-serif"
            }}
        >
            <div className="flex flex-col gap-4">
                <div className="grid grid-cols-1 gap-4 min-[390px]:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
                    <StatCard
                        title="Net P&L"
                        value={summary.netPnL}
                        helper={rangeLabel === 'All Time' ? 'Across your full journal' : rangeLabel}
                        infoDescription={dashboardDescriptions.netPnL}
                        tone={toNumber(summary.netPnL) >= 0 ? 'success' : 'danger'}
                        darkMode={darkMode}
                    />
                    <StatCard
                        title="Win %"
                        value={formatPercent(winRate)}
                        helper={`${summary.totalWins} wins / ${summary.totalLosses} losses`}
                        infoDescription={dashboardDescriptions.tradeWin}
                        tone="neutral"
                        darkMode={darkMode}
                    />
                    <StatCard
                        title="Largest Win"
                        value={summary.maxPnL}
                        helper="Best single trade in range"
                        infoDescription={dashboardDescriptions.largestWin}
                        tone="success"
                        darkMode={darkMode}
                    />
                    <StatCard
                        title="Day Win (%)"
                        value={formatPercent(dayWinRate)}
                        helper={`${summary.totalWinsPerDay} winning days`}
                        infoDescription={dashboardDescriptions.dayWin}
                        tone="neutral"
                        darkMode={darkMode}
                    />
                    <StatCard
                        title="Avg Trades/day"
                        value={averageTradesPerDay.toFixed(2)}
                        helper={`${activeDays} active trading days`}
                        infoDescription={dashboardDescriptions.averageTradesPerDay}
                        tone="neutral"
                        darkMode={darkMode}
                    />
                    <StatCard
                        title="Discipline %"
                        value={disciplineData ? formatPercent(disciplinePercent) : '--'}
                        helper={
                            disciplineData
                                ? `${disciplineEvaluatedTrades} evaluated trades`
                                : 'Private dashboard insight'
                        }
                        infoDescription={DASHBOARD_INFO.disciplinePercent}
                        tone="neutral"
                        darkMode={darkMode}
                    />
                </div>

                <div className="grid grid-cols-1 gap-4 min-[390px]:grid-cols-2 xl:grid-cols-4">
                    <SparkCard
                        title="Average Winner"
                        value={formatCurrency(averageWinner)}
                        tone="success"
                        valueTone="neutral"
                        infoDescription={dashboardDescriptions.averageWinner}
                        points={averageWinnerSeries}
                        showChart={false}
                        darkMode={darkMode}
                    />
                    <RatioCard
                        value={averageWinnerLoserRatio.toFixed(2)}
                        infoDescription={dashboardDescriptions.averageWinnerLoser}
                        winnerValue={averageWinner}
                        loserValue={averageLoser}
                        darkMode={darkMode}
                    />
                    <SparkCard
                        title="Average Loser"
                        value={formatAbsoluteCurrency(averageLoser)}
                        tone="success"
                        valueTone="neutral"
                        infoDescription={dashboardDescriptions.averageLoser}
                        points={averageLoserSeries}
                        showChart={false}
                        darkMode={darkMode}
                    />
                    <SparkCard
                        title="Largest Loss"
                        value={formatAbsoluteCurrency(largestLoss)}
                        tone="danger"
                        valueTone="danger"
                        infoDescription={dashboardDescriptions.largestLoss}
                        points={largestLossSeries}
                        showChart={false}
                        darkMode={darkMode}
                    />
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <AreaChartCard
                        title="Net P&L"
                        points={runningPnlSeries.length ? runningPnlSeries : dailySeries}
                        tone="success"
                        infoDescription={dashboardDescriptions.runningPnl}
                        darkMode={darkMode}
                    />
                    <PositiveNegativeBarCard
                        title="Daily P&L"
                        points={dailyBarPoints}
                        positiveKey="positive"
                        negativeKey="negative"
                        infoDescription={dashboardDescriptions.dailyPnl}
                        darkMode={darkMode}
                    />
                    <PositiveNegativeBarCard
                        title="Daily Win Rate"
                        points={dailyBarPoints}
                        positiveKey="wins"
                        negativeKey="losses"
                        infoDescription={DASHBOARD_INFO.dailyWinRate}
                        darkMode={darkMode}
                    />
                    <PerformanceGapChartCard
                        title="Discipline Performance Gap"
                        data={performanceGapChartData}
                        infoDescription={DASHBOARD_INFO.performanceGap}
                        darkMode={darkMode}
                    />
                </div>

                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
                    <InfoTableCard
                        title="Trades info"
                        description={`You made ${summary.totalTrades} trades in this range.`}
                        rows={tradesRows}
                        darkMode={darkMode}
                    />
                    <InfoTableCard
                        title="Performance info"
                        description="All performance data for your trades."
                        rows={performanceRows}
                        darkMode={darkMode}
                    />
                    <InfoTableCard
                        title="Symbols info"
                        description="Top symbols and outcomes across the current range."
                        rows={symbolsRows}
                        className="lg:col-span-2 xl:col-span-1"
                        darkMode={darkMode}
                    />
                </div>

                {company === 'Trading Terminal' && (
                    <p className="pt-1 text-left text-xs text-muted-foreground">
                        Commissions, ECN and regulatory fees are calculated at $0.005 per share,
                        with a $0.35 per ticket fee, to simulate realistic trading conditions
                        according to{' '}
                        <a
                            href="https://bearbulltraders.com/ibkr"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-semibold text-primary"
                        >
                            this
                        </a>
                        .
                    </p>
                )}
            </div>
        </div>
    );
}
